import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DirectionModel } from '../../models/master-list/direction.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { CustomerPricesheet, CustomerReviewModel } from '../../models/billing/customer-review.model';
import { BillingService } from '../../services/billing/billing.service';
import { HelpService } from '../../services/help.service';
import { CustomerReviewStatusModel } from '../../models/master-list/customer-review-status.model';
import { ShipmentMasterService } from '../../services/shipment-master.service';
import { ExpenseCodeModel } from "../../models/customer-maintenance/invoice/expense-code.model";
import { forkJoin, Subscription } from "rxjs";
import { ShipmentReviewModel } from "../../models/billing/shipment-review.model";
import { InvoiceService } from "../../services/customer-maintenance/invoice.service";

@Component({
    selector: 'app-update-form',
    templateUrl: './update-form.component.html',
    styleUrls: ['./update-form.component.scss'],
    standalone: false
})
export class UpdateFormComponent implements OnInit, OnDestroy {

  @Input() directions: DirectionModel[];
  @Input() customerReview: CustomerReviewModel;
  expenseCodes: ExpenseCodeModel[];
  customerReviewForm: FormGroup;
  shipmentReviews: ShipmentReviewModel[];
  isLoading = false;

  customerReviewStatus: CustomerReviewStatusModel[];

  private manageShipmentReviewSubs: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly invoiceService: InvoiceService,
    private readonly authService: AuthService,
    private readonly billingService: BillingService,
    private readonly helpService: HelpService,
    private readonly masterShipmentService: ShipmentMasterService
  ) { }

  ngOnInit(): void {
    this.manageMasterListServices();
    this.createCustomerReviewForm();
  }

  ngOnDestroy(): void {
    [this.manageShipmentReviewSubs]
      .forEach(sub => sub?.unsubscribe());
  }

  updateCustomerReview(): void {
    this.isLoading = true;

    this.manageShipmentReviewSubs = this.billingService.insertShipmentReview(this.customerReviewForm.value).subscribe({
      next: () => {
        this.helpService.showMessageNotification('Customer Review successfully Updated', 'success');
        this.isLoading = false;
        this.manageMasterListServices();
      },
      error: () => {
        this.helpService.showMessageNotification('Error! Please try again.', 'danger');
        this.isLoading = false;
      }
    });
  }

  private manageMasterListServices() {
    this.isLoading = true;
    const shipmentReviews$ = this.billingService.getShipmentReview(this.customerReview.shipmentID, 'C');
    const expenseCodes$ = this.invoiceService.getExpenseCodes(this.customerReview.customerID);
    const customerReviewStatus$ = this.masterShipmentService.getCustomerReviewStatuses();

    forkJoin([shipmentReviews$, expenseCodes$, customerReviewStatus$])
      .subscribe({
        next: ([shipmentReviews, expenseCodes, customerReviewStatus]) => {
          this.shipmentReviews = shipmentReviews
          this.customerReviewStatus = customerReviewStatus;
          this.expenseCodes = expenseCodes || [];
          this.isLoading = false;
        },
        error: () => {
          this.helpService.showMessageNotification('Error! Please try again', 'danger');
          this.isLoading = false;
        }
      });
  }

  private createCustomerReviewForm(): void {
    this.customerReviewForm = this.formBuilder.group({
      ShipmentID: [ this.customerReview.shipmentID ],
      ReviewType: [ 'C' ],
      Comment: [ '', Validators.required ],
      ExpenseCodesID: [ null ],
      DirectionID: [ null ],
      CustomerReviewStatusID: [ this.openCustomerPricesheet.customerReviewStatusID, Validators.required ],
      ManualAuditStatusID: [ null ],
      CreateDate: [ new Date() ],
      CreateUser: [ this.authService.userFullName ]
    });
  }

  get openCustomerPricesheet(): CustomerPricesheet {
    return this.customerReview.customerPricesheets.find((pricesheet: CustomerPricesheet)=>
      pricesheet.customerPriceSheetTypeID === 1 && pricesheet.customerInvoiceStatusID === 1);
  }

}
